/**
 * The invoice-dashboard export document (Rechnungen 2.0 — R8).
 *
 * PURE. No request, no React, no DOM — this module turns rows the caller
 * already holds into a CSV string and a filename. The collecting and the
 * downloading are `useInvoiceDashboardExport`'s job, and keeping them apart is
 * what makes every rule below testable without a browser.
 *
 * THE FILE IS SOURCE- AND VIEW-SCOPED, LIKE THE SCREEN IT CAME FROM. Bexio and
 * Vantio invoices are never in one document, and invoices and payments are
 * never in one document: two id spaces, two status vocabularies, and in the
 * payment case two different things entirely. The filename says which of the
 * three a file is, so no row needs a column repeating it.
 *
 * TWO CELL HELPERS, AND THE DIFFERENCE BETWEEN THEM IS LOAD-BEARING. Excel
 * evaluates a cell that begins with `=`, `+`, `-` or `@`. Several values here
 * are written by people outside this company — a Bexio invoice title, a
 * tenant's own name — so `csvTextCell` neutralises them with a leading
 * apostrophe. `csvNumericCell` deliberately does NOT: `-700.00` is a legitimate
 * negative payment, and an apostrophe there would turn a number into text in
 * every spreadsheet that opened it.
 *
 * MONEY IS A STRING FROM END TO END. The backend serialises decimals as strings
 * so no float sits between the database and the screen; this module writes them
 * out unchanged. A `Number()` anywhere on this path would silently turn
 * `"0.10"` into `0.1` and `"1234567890.12"` into something worse.
 *
 * THE PERIOD IS THE ONE FIELD THAT MAY NOT BE GUESSED. An invoice raised on
 * 2 October can bill September, so the issue date is not a fallback for the
 * billing month. R3 states whether the period is `authoritative`, `unavailable`
 * or `ambiguous`; anything but the first leaves the cell empty, and the quality
 * column says why.
 *
 * WHAT IS DELIBERATELY NOT IN THE FILE. No total row — the Bexio payment mirror
 * is upsert-only and any sum over it overstates cash. No `tenancy_id` and no
 * internal ids: they are technical handles, not operator-facing facts, and data
 * minimisation applies to a file that leaves the building (Regel 8). No
 * `billing_contact_name`, which is the raw provider contact and often a company.
 * No `bank_account_id` and no `synced_at`, neither of which R6a projects as a
 * business fact.
 */
import { todayBusinessDate } from "../../../utils/datetime";

/**
 * How many rows one export request asks for.
 *
 * Both list endpoints cap `limit` at 200 (`Query(default=25, ge=1, le=200)`);
 * asking for more is a 422, not a bigger page.
 */
export const EXPORT_PAGE_SIZE = 200;

const SEPARATOR = ";";
const EOL = "\r\n";
const BOM = "﻿";

/** The characters Excel reads as the start of a formula. */
const FORMULA_PREFIXES = ["=", "+", "-", "@", "\t", "\r", "\n"];

/**
 * One text cell: formula-neutralised first, then quoted if it needs to be.
 *
 * The order matters. Neutralising after quoting would put the apostrophe
 * outside the quotes, where it is part of the CSV syntax rather than of the
 * value — and Excel would evaluate the cell anyway.
 */
export function csvTextCell(value) {
  if (value == null) return "";
  let s = String(value);
  if (s === "") return "";
  if (FORMULA_PREFIXES.includes(s[0])) s = `'${s}`;
  if (/[";\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/**
 * One numeric cell: the wire value, verbatim.
 *
 * Deliberately not parsed. The value arrives as a decimal string and leaves as
 * the same decimal string, so precision cannot be lost between the two. And
 * deliberately NOT formula-escaped — see the module docstring.
 */
export function csvNumericCell(value) {
  if (value == null || value === "") return "";
  return String(value);
}

/** One boolean cell, in the language of the rest of the file. */
export function csvBooleanCell(value) {
  if (value == null) return "";
  return value ? "ja" : "nein";
}

/** An ISO date straight off the wire, or an empty cell. */
function csvDateCell(value) {
  return value == null || value === "" ? "" : String(value);
}

/**
 * The billing month, but only where the server was willing to name one.
 *
 * A period object can be present while `period_status` says the claim is not
 * authoritative — that combination means "we found candidates and they
 * disagree", and printing one of them would present a coin toss as a fact.
 */
function periodCell(row) {
  if (row.period_status !== "authoritative" || !row.period) return "";
  const month = String(row.period.month).padStart(2, "0");
  return `${row.period.year}-${month}`;
}

const COMMON_INVOICE_COLUMNS = [
  { header: "Rechnungsnummer", get: (r) => r.invoice_number },
  { header: "Mieter", get: (r) => r.tenant?.name },
  { header: "Objekt", get: (r) => r.unit?.label },
  { header: "Zimmer", get: (r) => r.room?.name },
  { header: "Betrag", numeric: true, get: (r) => r.amount },
  { header: "Währung", get: (r) => r.currency },
  { header: "Offener Betrag", numeric: true, get: (r) => r.open_amount },
  { header: "Status", get: (r) => r.canonical_status },
  { header: "Rechnungsdatum", date: true, get: (r) => r.issue_date },
  { header: "Fälligkeit", date: true, get: (r) => r.due_date },
  { header: "Periode", get: periodCell },
  { header: "Periode-Qualität", get: (r) => r.period_status },
];

/**
 * The Bexio columns, plus what only a mirrored provider document can answer.
 *
 * `provider_status_raw` is the provider's own word before it was mapped onto the
 * shared axis — the column that lets an operator see what the system actually
 * read.
 */
export const INVOICE_EXPORT_COLUMNS_BEXIO = [
  ...COMMON_INVOICE_COLUMNS,
  { header: "Provider-Status", get: (r) => r.provider_status_raw },
  { header: "Titel", get: (r) => r.bexio?.title },
  { header: "Bexio-Beleg-ID", numeric: true, get: (r) => r.bexio?.external_id },
];

/**
 * The native columns.
 *
 * No provider-specific columns at all, rather than three that could only ever
 * be empty: a column that is always blank teaches a reader to ignore it.
 * `Offener Betrag` stays, because the CONTRACT has the field and states it is
 * always null here — the empty cell is the honest answer, not a missing one.
 */
export const INVOICE_EXPORT_COLUMNS_NATIVE = [...COMMON_INVOICE_COLUMNS];

/**
 * The payment columns.
 *
 * The four data-quality flags get their own columns because the table shows
 * them as badges; a file without them would quietly drop the warnings that are
 * the whole reason those rows stay visible. `missing_payment_date` needs no
 * column — the empty date cell already says it.
 */
export const PAYMENT_EXPORT_COLUMNS = [
  { header: "Zahlungs-ID", numeric: true, get: (r) => r.bexio_payment_id },
  { header: "Rechnungsnummer", get: (r) => r.invoice?.document_nr },
  { header: "Rechnungstitel", get: (r) => r.invoice?.title },
  { header: "Mieter", get: (r) => r.tenant?.name },
  { header: "Objekt", get: (r) => r.unit?.label },
  { header: "Zimmer", get: (r) => r.room?.name },
  { header: "Betrag", numeric: true, get: (r) => r.amount },
  { header: "Währung", get: (r) => r.currency },
  { header: "Zahlungsdatum", date: true, get: (r) => r.payment_date },
  { header: "Rechnung auflösbar", boolean: true, get: (r) => r.data_quality?.invoice_resolved },
  { header: "Rechnung storniert", boolean: true, get: (r) => r.data_quality?.invoice_cancelled },
  { header: "Negativbetrag", boolean: true, get: (r) => r.data_quality?.negative_value },
  { header: "Währung unbekannt", boolean: true, get: (r) => r.data_quality?.unknown_currency },
];

function cellFor(column, row) {
  const raw = column.get(row);
  if (column.numeric) return csvNumericCell(raw);
  if (column.boolean) return csvBooleanCell(raw);
  if (column.date) return csvDateCell(raw);
  return csvTextCell(raw);
}

/**
 * Header plus one line per row, in the order the caller handed them over.
 *
 * No sorting and no filtering here: the server chose the order and the
 * population, and a second opinion in the browser would make the file disagree
 * with the table it came from.
 */
function buildCsv(columns, rows) {
  const list = Array.isArray(rows) ? rows : [];
  const header = columns.map((c) => csvTextCell(c.header)).join(SEPARATOR);
  const body = list.map((row) =>
    columns.map((c) => cellFor(c, row)).join(SEPARATOR)
  );
  return BOM + [header, ...body].join(EOL) + EOL;
}

/** One source's invoices as a CSV document. */
export function buildInvoiceCsv(rows, source) {
  const columns =
    source === "bexio" ? INVOICE_EXPORT_COLUMNS_BEXIO : INVOICE_EXPORT_COLUMNS_NATIVE;
  return buildCsv(columns, rows);
}

/** The Bexio payment rows as a CSV document. */
export function buildPaymentCsv(rows) {
  return buildCsv(PAYMENT_EXPORT_COLUMNS, rows);
}

/** How each combination names itself on disk. */
const FILE_STEMS = {
  "invoices:bexio": "rechnungen-bexio",
  "invoices:native": "rechnungen-vantio",
  "payments:bexio": "zahlungen-bexio",
};

/**
 * A deterministic filename, dated by the SWISS business day.
 *
 * Not `new Date().getFullYear()` and not an ISO slice of a UTC instant: between
 * Zurich midnight and UTC midnight those name yesterday, and the business day
 * has to be the same for a reader in Zurich and one in Istanbul (Regel 51).
 *
 * Nothing from the filter bar reaches the name — no search term, no tenant, no
 * month. A filename travels through mail clients and shared folders, and a
 * tenant's name in one is a leak with no upside.
 */
export function exportFilename({ view, source, now } = {}) {
  const stem = FILE_STEMS[`${view}:${source}`];
  if (!stem) return null;
  return `${stem}-${todayBusinessDate(now)}.csv`;
}
